<template>
  <div class="card card-white">
    <BaseDataTable
      :columns="columns"
      :entries="entries"
      :loading="isLoading"
      :default-page-length="10"
      :default-page-length-options="[10, 25, 50]"
      class="candidates-table"
      tall-rows
    >
      <template #no-results>
        <div>
          {{ isLoading ? $t('Loading') : $t("You don't have any candidates yet.") }}
        </div>
      </template>
      <template #freelancer="{ row, rowIndex }">
        <WdsAvatarLabel
          :path="getAvatarPath(row.hasPicture ? row.picture : undefined, row.id)"
          :text="row.fullName"
          :text-link="row.profileUrl"
          :sub-text="row.email"
          :data-test="`recruiter-contact-avatar-${rowIndex}`"
        />
      </template>
      <template #jobTitle="{ row, rowIndex }">
        <div :data-test="`recruiter-contact-job-title-${rowIndex}`">
          {{ row.jobTitle }}
        </div>
      </template>
      <template #attachments="{ row, rowIndex }">
        <RowPopoverList
          :title="$t('Attachments')"
          title-icon="file"
          :items="row.filesForPopover"
          :data-test="`recruiter-contact-attachments-${rowIndex}`"
        />
      </template>
      <template #links="{ row, rowIndex }">
        <RowPopoverList
          :title="$t('Links')"
          title-icon="link"
          :items="row.linksForPopover"
          :data-test="`recruiter-contact-links-${rowIndex}`"
        />
      </template>
      <template #rates="{ row, rowIndex }">
        <div :data-test="`recruiter-contact-rates-${rowIndex}`">
          <div v-if="row.displayRates.length">
            <WdsTooltip v-if="row.displayRates.length > 1" class="tw-z-20">
              <template #tooltip-content>
                <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-600 tw-mb-2">
                  <WdsIcon name="money" :size="16" />
                  {{ $t('Rates') }}
                </div>
                <div
                  v-for="displayRate in row.displayRates"
                  :key="displayRate.rateType"
                  class="tw-text-left tw-mb-1"
                  :data-test="`recruiter-contact-${displayRate.rateType}-${rowIndex}`"
                >
                  {{ `${row.currency}  ${displayRate.rate} ` }}
                  <span class="tw-lowercase">{{ displayRate.rateType }}</span>
                </div>
              </template>
              <WdsLabel subtle sm class="tw-lowercase">
                {{ `${row.displayRates.length}  ${$t('Rates')}` }}
              </WdsLabel>
            </WdsTooltip>
            <div v-else>
              <WdsLabel subtle sm class="tw-mr-1.5">
                {{ `${row.currency}  ${row.displayRates[0].rate}` }}
              </WdsLabel>
              <span class="tw-text-gray-800 tw-lowercase">{{ row.displayRates[0].rateType }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #tags="{ row, rowIndex }">
        <div v-if="row.visibleTags.length" :data-test="`recruiter-contact-tags-${rowIndex}`">
          <WdsLabel v-for="tag in row.visibleTags" :key="tag" subtle sm class="tw-mr-2">
            {{ tag }}
          </WdsLabel>
          <WdsTooltip v-if="row.additionalTags.length" class="tw-z-20">
            <template #tooltip-content>
              <div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-600 tw-mb-2">
                <WdsIcon name="tag" :size="16" />
                {{ $t('Tags') }}
              </div>
              <div
                v-for="additionalTag in row.additionalTags"
                :key="additionalTag"
                class="tw-text-left tw-mb-1"
              >
                {{ additionalTag }}
              </div>
            </template>
            <WdsLabel subtle sm>
              {{ `+${row.additionalTags.length}` }}
            </WdsLabel>
          </WdsTooltip>
        </div>
        <div v-else>-</div>
      </template>
      <template v-if="user.hasPermission('manage-candidates')" #actions="{ row, rowIndex }">
        <RecruiterFreelancerMenu
          :id="row.pivotId"
          :name="row.name"
          :email="row.email"
          :currency="row.currency"
          :rates="row.rates"
          :job-title="row.jobTitle"
          :tags="row.tags"
          :links="row.links"
          :files="row.files"
          :data-test="`recruiter-contact-menu-${rowIndex}`"
        />
      </template>
    </BaseDataTable>
  </div>
</template>

<script>
import { WdsAvatarLabel, WdsLabel, WdsIcon, WdsTooltip } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable'
import RowPopoverList from '@/components/Contacts/RecruiterFreelancer/RowPopoverList.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import Urls from '@/utils/urls'
import RecruiterFreelancerMenu from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerMenu.vue'
import { getAvatarPath } from '@/utils/avatar'

export default {
  name: 'RecruiterFreelancerContacts',
  components: {
    RecruiterFreelancerMenu,
    BaseDataTable,
    WdsAvatarLabel,
    WdsLabel,
    WdsIcon,
    WdsTooltip,
    RowPopoverList,
  },
  props: {
    candidates: {
      type: Array,
      default: () => [],
    },
    apolloLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      namedRoutes,
      open: true,
      columns: [
        {
          slot: 'freelancer',
          key: 'fullName',
          title: 'Freelancer',
          orderAble: true,
          searchKeys: ['fullName', 'email'],
        },
        {
          slot: 'jobTitle',
          key: 'jobTitle',
          title: 'Job title',
          class: 'vmiddle',
          orderAble: true,
          searchKeys: ['jobTitle'],
        },
        {
          slot: 'attachments',
          key: 'attachments',
          title: 'Attachments',
          class: 'vmiddle',
          orderAble: true,
          sort: 'attachments.length',
        },
        {
          slot: 'links',
          key: 'links',
          title: 'Links',
          class: 'vmiddle',
          orderAble: true,
          sort: 'links.length',
        },
        {
          slot: 'rates',
          key: 'rates',
          title: 'Rates',
          class: 'vmiddle',
          sort: 'visibleRates.length',
        },
        {
          slot: 'tags',
          key: 'tags',
          title: 'Tags',
          class: 'vmiddle',
          searchKeys: ['tags'],
        },
        {
          slot: 'actions',
          key: 'actions',
          title: '',
          class: 'vmiddle',
        },
      ],
      loading: false,
      maxTags: 2,
    }
  },
  computed: {
    entries() {
      return this.candidates.map((result, index) => {
        const tags = result.tags.map((tag) => tag.name)

        return {
          id: result.worker.id,
          picture: result.worker.avatar,
          hasPicture: result.worker.avatar !== null,
          name: {
            firstName: result.worker.firstName,
            middleName: result.worker.middleName,
            lastName: result.worker.lastName,
          },
          fullName: result.worker.name,
          status: result.status,
          email: result.email,
          pivotId: result.id,
          currency: result.worker.currency,
          rates: {
            hourly: result.hourRate,
            daily: result.dailyRate,
            monthly: result.monthlyRate,
          },
          jobTitle: result.jobTitle,
          profileUrl: result.worker.profile?.url,
          displayRates: this.getDisplayRates(result.hourRate, result.dailyRate, result.monthlyRate),
          tags,
          visibleTags: this.getVisibleTags(tags),
          additionalTags: this.getAdditionalTags(tags),
          links: result.links.map((link) => ({
            text: link,
            url: link,
          })),
          linksForPopover: result.links.map((link) => ({
            text: link,
            url: Urls.formatWithProtocol(link),
          })),
          files: result.files.data,
          filesForPopover: result.files.data.map(({ name, id }) => ({
            text: name,
            url: laravelNamedRoute(namedRoutes.files.download, { fileId: id }),
          })),
        }
      })
    },
    isLoading() {
      return this.loading || this.apolloLoading
    },
  },
  methods: {
    getAvatarPath,
    laravelNamedRoute,
    toggleLoading() {
      this.loading = !this.loading
    },
    deleted() {
      this.loading = false
    },
    getVisibleTags(tags) {
      return tags.slice(0, this.maxTags)
    },
    getAdditionalTags(tags) {
      return tags.slice(this.maxTags, tags.length)
    },
    getDisplayRates(hourlyRate, dailyRate, monthlyRate) {
      const displayRates = []
      if (hourlyRate) {
        displayRates.push({
          rate: hourlyRate,
          rateType: $t('Per hour'),
        })
      }
      if (dailyRate) {
        displayRates.push({
          rate: dailyRate,
          rateType: $t('Per day'),
        })
      }
      if (monthlyRate) {
        displayRates.push({
          rate: monthlyRate,
          rateType: $t('Per month'),
        })
      }
      return displayRates
    },
  },
}
</script>

<style lang="scss">
.candidates-table {
  overflow-x: visible !important;
}
</style>
